/**
 * WithdrawBuddyGroupModal — Confirmation dialog for withdrawing a buddy
 * group from ONE rodeo without touching the group itself.
 *
 * Withdrawing is not a destructive action on entries: the group's
 * registration for that rodeo is removed, the roster stays intact for
 * other rodeos, and any entries made under the group survive as ordinary
 * solo entries. That distinction is the whole point of the dialog, so it
 * loads a server-side impact preview and:
 *
 *   - always states that the group and its members survive,
 *   - renders an extra RED warning naming every live entry that will be
 *     decoupled into a solo entry (only when there is at least one),
 *   - explains up front when the withdrawal is blocked (draw already
 *     run, group not registered here) instead of failing after confirm.
 */

import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle, AlertTriangle, Loader2, LogOut, Users } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { buddyGroupOpsKeys } from '@/api/extensions/useBuddyGroupOps';
import {
  detachBuddyGroupFromRodeo,
  previewWithdrawBuddyGroupFromRodeo,
  type WithdrawBuddyGroupPreview,
} from '@/services/buddy-group.service';
import { useToast } from '@/contexts/ToastContext';

interface WithdrawBuddyGroupModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  groupId: string;
  groupName: string | null;
  rodeoId: string;
  /**
   * Fired after a successful withdrawal so the host surface can refresh
   * or navigate. The modal has already closed and toasted by then.
   */
  onWithdrawn?: (result: { cleared_registration_count: number }) => void;
}

export function WithdrawBuddyGroupModal({
  open,
  onOpenChange,
  groupId,
  groupName,
  rodeoId,
  onWithdrawn,
}: WithdrawBuddyGroupModalProps) {
  const qc = useQueryClient();
  const { showSuccess, showError } = useToast();

  const [preview, setPreview] = useState<WithdrawBuddyGroupPreview | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoadingPreview(true);
    setError(null);
    previewWithdrawBuddyGroupFromRodeo(groupId, rodeoId)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err: any) => {
        if (!cancelled) {
          setError(err?.message ?? 'Failed to load withdrawal preview');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingPreview(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, groupId, rodeoId]);

  const handleClose = () => {
    if (!submitting) {
      onOpenChange(false);
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await detachBuddyGroupFromRodeo(groupId, rodeoId);
      qc.invalidateQueries({ queryKey: buddyGroupOpsKeys.all });
      qc.invalidateQueries({ queryKey: ['buddy-group', groupId] });
      qc.invalidateQueries({ queryKey: ['buddy-groups'] });
      qc.invalidateQueries({ queryKey: ['buddy-group-rodeo-attachments'] });
      qc.invalidateQueries({ queryKey: ['registration-ops'] });
      qc.invalidateQueries({ queryKey: ['event-registrations'] });

      const count = result.cleared_registration_count;
      showSuccess(
        'Buddy group withdrawn',
        count > 0
          ? `The group is still intact. ${count} event ${
              count === 1 ? 'entry is' : 'entries are'
            } now ${count === 1 ? 'a solo entry' : 'solo entries'} — nothing was cancelled.`
          : 'The group is still intact and had no event entries for this rodeo.',
      );

      onOpenChange(false);
      onWithdrawn?.({ cleared_registration_count: count });
    } catch (err: any) {
      const msg = err?.message ?? 'Failed to withdraw buddy group';
      setError(msg);
      showError('Could not withdraw', msg);
    } finally {
      setSubmitting(false);
    }
  };

  const displayGroupName = preview?.group_name ?? groupName ?? 'this buddy group';
  const displayRodeoName = preview?.rodeo_name ?? 'this rodeo';
  const entryCount = preview?.entry_count ?? 0;
  const blocked = !!preview && !preview.can_withdraw;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogOut className="h-5 w-5" />
            Withdraw from rodeo
          </DialogTitle>
          <DialogDescription>
            Withdraw <strong>{displayGroupName}</strong> from{' '}
            <strong>{displayRodeoName}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 text-sm">
          {loadingPreview ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading impact preview…
            </div>
          ) : preview ? (
            <>
              {/* Reassurance first — this is the thing captains are most
                  worried about, and the reason they avoid the button. */}
              <div className="rounded-md border border-border bg-muted/40 p-3">
                <div className="mb-1 flex items-center gap-2 font-medium">
                  <Users className="h-4 w-4" />
                  The group itself is kept
                </div>
                <p className="text-muted-foreground">
                  {displayGroupName} and its{' '}
                  {preview.member_count === 1
                    ? '1 member'
                    : `${preview.member_count} members`}{' '}
                  stay exactly as they are, and the group can still be entered in
                  other rodeos. Only its registration for {displayRodeoName} is
                  removed.
                </p>
              </div>

              {blocked ? (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3">
                  <div className="mb-1 flex items-center gap-2 font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4" />
                    This group can't be withdrawn right now
                  </div>
                  <p className="text-foreground">
                    {preview.blocked_reason ??
                      'Withdrawal is not available for this rodeo.'}
                  </p>
                </div>
              ) : entryCount > 0 ? (
                <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3">
                  <div className="mb-1 flex items-center gap-2 font-medium text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    {entryCount} event {entryCount === 1 ? 'entry' : 'entries'} will
                    become {entryCount === 1 ? 'a solo entry' : 'solo entries'}
                  </div>
                  <p className="text-foreground">
                    These entries are <strong>not</strong> cancelled or removed —
                    they stay entered, but they will no longer be tied to the buddy
                    group, so the group's performance preferences stop applying to
                    them. Cancelling an entry is a separate action.
                  </p>
                  <ul className="mt-2 list-disc space-y-0.5 pl-5 text-foreground">
                    {preview.entries.map((e) => (
                      <li key={e.registration_id}>
                        {e.member_name ?? 'A member'} — {e.event_name ?? 'Event'}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs text-muted-foreground">
                    Every member of the group will be notified that their entries
                    are now solo entries.
                  </p>
                </div>
              ) : (
                <p className="text-muted-foreground">
                  No event entries are linked to this group for {displayRodeoName},
                  so nothing else changes.
                </p>
              )}
            </>
          ) : null}

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={submitting || loadingPreview || blocked || (!!error && !preview)}
          >
            {submitting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="mr-2 h-4 w-4" />
            )}
            Withdraw from rodeo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
